import { useContext, useEffect, useRef, useState } from 'react';
import SMSContext from '../../contexts/data/SMSContext';
import { useWindowSize } from '@leedpulse-monorepo/hooks';
import { Table, Tag, Modal, Button, Flex } from 'antd';
import { DownloadOutlined, EyeOutlined, UploadOutlined } from '@ant-design/icons';
import SMSWithContact from '../../../domain/models/@aggregates/SMSWithContact';
import { CONTACTS_PAGE_PATH } from '../../pages/contacts/ContactsPage';
import moment from 'moment';
import { getInterestLevel } from '@leedpulse-monorepo/utils';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { advancedOnRow, getTagColor } from '../../../core/utils/commonUtils';
import { useNavigate } from 'react-router-dom';
import { ESmsRole, smsRoleAsString } from '@leedpulse-monorepo/enums';

interface SMSListProps {
  scopedSubscriptionId: string | null;
}

const SMSList: React.FC<SMSListProps> = ({ scopedSubscriptionId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentData, setCurrentData] = useState([] as SMSWithContact[]);
  const [selectedSMS, setSelectedSMS] = useState<SMSWithContact | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const smsContext = useContext(SMSContext);
  const windowSize = useWindowSize();
  const fetchNextCalled = useRef(false);

  const navigate = useNavigate();

  const handleViewDetails = (record: SMSWithContact, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedSMS(record);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedSMS(null);
  };

  const handlePaginationChange = (page: number) => {
    setCurrentPage(page);
    const currentSlice = smsContext.items.slice(
      (page - 1) * smsContext.hitsPerPage,
      page * smsContext.hitsPerPage
    );
    if (currentSlice.length === 0) {
      fetchNextCalled.current = true;
      smsContext.fetchNext();
    }
  };

  const handlePageSizeChange = (_current: number, newPageSize: number) => {
    setCurrentPage(1);
    smsContext.fetch(scopedSubscriptionId, newPageSize);
  };

  // This hook is used to initialize the sms list
  useEffect(() => {
    if (
      smsContext.status === ContextStatus.IDLE ||
      smsContext.scopedSubscriptionId !== scopedSubscriptionId
    ) {
      smsContext.fetch(scopedSubscriptionId);
    }
    return () => {
      smsContext.reset();
    };
  }, []);

  // This hook is triggered when a 'page next' is called to get the new slice of items
  useEffect(() => {
    if (smsContext.status === ContextStatus.SUCCESS) {
      const currentSlice = smsContext.items.slice(
        (currentPage - 1) * smsContext.hitsPerPage,
        currentPage * smsContext.hitsPerPage
      );
      // Synchro avec un contexte qui fetche : l'etat local suit le resultat d'un
      // chargement declenche ici. Architecture Context + use case du repo.
      // eslint-disable-next-line react/set-state-in-effect
      setCurrentData(currentSlice);
      fetchNextCalled.current = false;
    }
  }, [smsContext.status]);

  // This hook is triggered when the current page changes to update the displayed data
  useEffect(() => {
    if (!fetchNextCalled.current) {
      const currentSlice = smsContext.items.slice(
        (currentPage - 1) * smsContext.hitsPerPage,
        currentPage * smsContext.hitsPerPage
      );
      setCurrentData(currentSlice);
    }
  }, [currentPage]);

  // This hook is used to go back to first page when refreshing the list
  // Retour a la page 1 quand la tranche courante est vide. Calcule pendant le
  // rendu (pattern « ajuster l'etat quand une prop change » de la doc React) :
  // dans un effet, chaque fin de chargement coutait un second rendu du tableau.
  const [lastStatusForClamp, setLastStatusForClamp] = useState(smsContext.status);
  if (smsContext.status !== lastStatusForClamp) {
    setLastStatusForClamp(smsContext.status);
    if (
      smsContext.status === ContextStatus.SUCCESS &&
      smsContext.items.slice(
        (currentPage - 1) * smsContext.hitsPerPage,
        currentPage * smsContext.hitsPerPage
      ).length === 0
    ) {
      setCurrentPage(1);
    }
  }

  return (
    <>
      <Table
        pagination={{
          current: currentPage,
          total: smsContext.count,
          pageSize: smsContext.hitsPerPage,
          onChange: handlePaginationChange,
          onShowSizeChange: (_current, newSize) => handlePageSizeChange(_current, newSize),
          showSizeChanger: true,
          simple: true,
          pageSizeOptions: [20, 50, 100, 200],
          position: ['bottomRight', 'topRight'],
          disabled: smsContext.status === ContextStatus.LOADING,
        }}
        loading={smsContext.status === ContextStatus.LOADING}
        columns={
          windowSize.width > 576
            ? [
              {
                title: 'Nom complet',
                dataIndex: 'contactFullname',
                key: 'contactFullname',
                render: (contactFullname: string, record: SMSWithContact) => (
                  <div>
                    <div>{contactFullname}</div>
                    <Tag className='contact-tile__content__owner-tag' color={getTagColor(record?.contact?.qualification_ai?.qualification || "not_available")}>
                      &#8226; {getInterestLevel(record?.contact?.qualification_ai?.qualification || "not_available")}
                    </Tag>
                  </div>
                ),
              },
              {
                title: 'Message',
                dataIndex: 'messagePreview',
                key: 'messagePreview',
                render: (messagePreview: string, record: SMSWithContact) => (
                  <div>{record.sms.role === ESmsRole.user ? <DownloadOutlined size={48} /> : <UploadOutlined size={48} />} {messagePreview}</div>
                ),
              },
              {
                title: 'Date',
                dataIndex: 'timestamp',
                key: 'timestamp',
                render: (timestamp: Date) => moment(timestamp).fromNow(),
              },
              {
                title: 'Actions',
                key: 'actions',
                width: 80,
                render: (_: unknown, record: SMSWithContact) => (
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={(e) => handleViewDetails(record, e)}
                    title="Voir les détails"
                  />
                ),
              },
            ]
            : [
              {
                title: 'Nom complet',
                dataIndex: 'contactFullname',
                key: 'contactFullname',
              },
              {
                title: 'Message',
                dataIndex: 'messagePreview',
                key: 'messagePreview',
                render: (messagePreview: string, record: SMSWithContact) => (
                  <div>{record.sms.role === ESmsRole.user ? <DownloadOutlined size={48} /> : <UploadOutlined size={48} />} {messagePreview}</div>
                ),
              },
              {
                title: 'Date',
                dataIndex: 'timestamp',
                key: 'timestamp',
                render: (timestamp: Date) => moment(timestamp).fromNow(),
              },
              {
                title: 'Actions',
                key: 'actions',
                width: 80,
                render: (_: unknown, record: SMSWithContact) => (
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={(e) => handleViewDetails(record, e)}
                    title="Voir les détails"
                  />
                ),
              },
            ]
        }
        rowKey={(record) => record.id}
        dataSource={currentData}
        onRow={(record) => {
          const isUnknownContact = !record.contact.id || record.contact.id.trim() === '';
          return advancedOnRow({
            url: `${CONTACTS_PAGE_PATH}/${record.contact.id}`,
            navigate,
            onClickBefore: (event) => {
              if (isUnknownContact) return false;
            },
            onMouseDownBefore: (event) => {
              if (isUnknownContact) return false;
            },
            className: isUnknownContact ? 'table-row table-row--disabled' : 'table-row',
            style: isUnknownContact ? { cursor: 'default' } : { cursor: 'pointer' },
          })
        }}
      />
      <Modal
        title="Détails du SMS"
        open={isModalVisible}
        onCancel={handleCloseModal}
        destroyOnHidden={true}
        footer={[
          <Button key="close" onClick={handleCloseModal}>
            Fermer
          </Button>,
        ]}
      >
        {selectedSMS && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Type :</strong>
              <div style={{ marginTop: '4px' }}>
                <Tag color={selectedSMS.sms.role === ESmsRole.user ? 'blue' : 'green'}>
                  {selectedSMS.sms.role === ESmsRole.user ? <Flex align="center"><DownloadOutlined size={48} /> Message reçu</Flex> : selectedSMS.sms.role === ESmsRole.assistant ? <Flex align="center"><UploadOutlined size={48} /> Message envoyé</Flex> : smsRoleAsString(selectedSMS.sms.role)}
                </Tag>
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Nom complet :</strong>
              <div style={{ marginTop: '4px' }}>{selectedSMS.contactFullname}</div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Message complet :</strong>
              <div style={{ marginTop: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {selectedSMS.sms.message}
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Heure exacte :</strong>
              <div style={{ marginTop: '4px' }}>
                {moment(selectedSMS.sms.created).format('DD/MM/YYYY à HH:mm:ss')}
              </div>
            </div>
            {selectedSMS.sms.role === ESmsRole.user && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Numéro de l'expéditeur :</strong>
                <div style={{ marginTop: '4px' }}>
                  {selectedSMS.sms.customer || selectedSMS.contact.contact.phone || 'Non disponible'}
                </div>
              </div>
            )}
            {selectedSMS.sms.role === ESmsRole.assistant && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Numéro du destinataire :</strong>
                <div style={{ marginTop: '4px' }}>
                  {selectedSMS.contact.contact.phone || selectedSMS.sms.customer || 'Non disponible'}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default SMSList;
